import {
  Box,
  Grid,
  Typography,
  Card,
  Stack,
  Tooltip,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from 'util/currency';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import OrderSummaryItem from './order-summary-item.component';

const OrderSummaryInformation = data => {
  const { t } = useTranslation();

  const { orderDetails, customGridColumns } = data;
  return (
    <Box>
      <OrderSummaryItem
        label={t('drawer.orderDetailsDrawer.subTotal')}
        value={formatCurrency(
          orderDetails?.orderTotal?.preDiscountSubtotal ?? 0,
        )}
        testId={'OrderDetailsDrawer.displaySubTotal'}
      />
      {orderDetails?.orderDiscounts?.length > 0 && (
        <Grid container spacing={2} mt={0}>
          <Grid size={{ xs: 6 }}>
            <Stack direction={'row'} alignItems={'center'}>
              <Typography ml={2} variant="subtitle2" sx={{ fontWeight: '700' }}>
                {t('drawer.orderDetailsDrawer.discounts')}
              </Typography>
              <Tooltip
                title={orderDetails?.orderDiscounts?.map((discount, idx) => {
                  return (
                    <Typography
                      key={`${discount?.extDiscountId}_${idx}`}
                      data-testid={'OrderDetailsDrawer.orderDiscountToolTip'}
                    >
                      {`${discount.description}: ${formatCurrency(
                        discount?.appliedDiscount,
                      )}`}
                    </Typography>
                  );
                })}
              >
                <IconButton>
                  <InfoOutlined sx={{ fontSize: '16px', cursor: 'pointer' }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
          <Grid size={{ xs: 6 }}>
            <Typography
              mr={2}
              variant="subtitle2"
              gutterBottom
              sx={{
                fontWeight: '700',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
              data-testid={'OrderDetailsDrawer.orderDiscount'}
            >
              -
              {formatCurrency(
                orderDetails?.orderDiscounts?.reduce(
                  (acc, discount) => acc + discount.appliedDiscount,
                  0,
                ),
              )}
            </Typography>
          </Grid>
        </Grid>
      )}
      <OrderSummaryItem
        label={t('drawer.orderDetailsDrawer.tip')}
        value={formatCurrency(orderDetails?.orderTotal?.gratuity ?? 0)}
        testId={'OrderDetailsDrawer.displayGratuity'}
      />

      <Grid container spacing={2} mt={0}>
        <Grid size={{ xs: 6 }}>
          <Stack direction={'row'} alignItems={'center'}>
            <Typography ml={2} variant="subtitle2" sx={{ fontWeight: '700' }}>
              {t('drawer.orderDetailsDrawer.charges')}
            </Typography>
            {orderDetails?.orderTotal?.chargeSummary?.charges?.length > 0 && (
              <Tooltip
                title={orderDetails?.orderTotal?.chargeSummary?.charges?.map(
                  (charge, idx) => {
                    return (
                      <Typography
                        key={`${charge?.chargeId}_${idx}`}
                        data-testid={'OrderDetailsDrawer.chargesToolTip'}
                      >
                        {`${charge.chargeDescription}: ${formatCurrency(
                          charge.chargeApplied,
                        )}`}
                      </Typography>
                    );
                  },
                )}
              >
                <IconButton>
                  <InfoOutlined sx={{ fontSize: '16px', cursor: 'pointer' }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Grid>
        <Grid size={{ xs: 6 }}>
          <Typography
            mr={2}
            variant="subtitle2"
            gutterBottom
            sx={{
              fontWeight: '700',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
            data-testid={'OrderDetailsDrawer.charges'}
          >
            {formatCurrency(
              orderDetails?.orderTotal?.chargeSummary?.totalCharges ?? 0,
            )}
          </Typography>
        </Grid>
      </Grid>
      {orderDetails?.orderTotal?.taxSummary?.taxes?.map((tax, idx) => {
        return (
          <OrderSummaryItem
            key={idx}
            label={tax?.taxDescription}
            value={formatCurrency(tax?.taxAmountApplied ?? 0)}
            testId={'OrderDetailsDrawer.taxAmount'}
          />
        );
      })}

      <OrderSummaryItem
        label={t('drawer.orderDetailsDrawer.total')}
        value={formatCurrency(orderDetails?.orderTotal?.total ?? 0)}
        testId={'OrderDetailsDrawer.displayTotal'}
      />
      {orderDetails?.orderTotal?.refundSummary?.refunds.length > 0 && (
        <Grid container spacing={2} mt={0}>
          <Grid size={{ xs: 6 }}>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: '700' }}
            >
              {t('drawer.orderDetailsDrawer.refund')}
            </Typography>
          </Grid>
        </Grid>
      )}
      {orderDetails?.orderTotal?.refundSummary?.refunds?.map(
        (refundItem, idx) => {
          return (
            <Card
              key={idx}
              sx={{
                marginBottom: '16px',
              }}
            >
              <Grid container spacing={2} p={2}>
                <Grid size={{ xs: 12, md: customGridColumns ?? 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('drawer.orderDetailsDrawer.date')}
                  </Typography>
                  <Typography mr={2} variant="subtitle2" fontWeight={700}>
                    {refundItem?.refundDate}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: customGridColumns ?? 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('drawer.orderDetailsDrawer.refundAmount')}
                  </Typography>
                  <Typography mr={2} variant="subtitle2" fontWeight={700}>
                    {formatCurrency(refundItem?.refundAmount, 'USD')}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: customGridColumns ?? 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('drawer.orderDetailsDrawer.refundReason')}
                  </Typography>
                  <Typography mr={2} variant="subtitle2" fontWeight={700}>
                    {refundItem?.refundReason}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: customGridColumns ?? 3 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('drawer.orderDetailsDrawer.refundedBy')}
                  </Typography>
                  <Typography mr={2} variant="subtitle2" fontWeight={700}>
                    {`${refundItem?.userInfo?.firstName} ${refundItem?.userInfo?.lastName} ${refundItem?.userInfo?.email}`}
                  </Typography>
                </Grid>
              </Grid>
            </Card>
          );
        },
      )}
    </Box>
  );
};
export default OrderSummaryInformation;
